import { flatMap, cloneDeep, isEqual } from "lodash-es";
import { useCallback, useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "react-query";
import Auth from "shared/@auth";

/**
 * Just sequentially number data in the order they were received from the API.
 * This is bad, but you'll at least be able to see the problems when the page loads
 * and changes will be persisted when you save.
 */
function fixData(data) {
  const cloned = cloneDeep(data);

  for (const category of cloned) {
    let sortOrder = 1;

    for (const file of category.files) {
      file.sortOrder = sortOrder++;
    }
  }

  return cloned;
}

export function useSSFSortData() {
  const client = useQueryClient();
  const { makeAuthedRequest } = Auth.useContainer();

  const [categories, setCategories] = useState([]);
  const [originals, setOriginals] = useState([]);

  // This query key starts with 'cms' to be invalidated with the rest of the CMS data
  const { isSuccess, isLoading, isError, data, error } = useQuery(
    ["cms", "ssf-sort"],
    () => makeAuthedRequest(`/ssf-sort/categories`).then((res) => res.json()),
    { refetchOnMount: true }
  );

  useEffect(() => {
    if (data) {
      setCategories(fixData(data));
      setOriginals(data);
    }
  }, [data]);

  const reset = useCallback(() => {
    setCategories(fixData(data));
    setOriginals(data);
  }, [data]);

  const saveMutation = useMutation(
    () => {
      const current = flatMap(fixData(categories), (c) => c.files);
      const original = flatMap(originals, (c) => c.files);

      const changed = current.filter(
        (c) => original.find((o) => o.id === c.id).sortOrder !== c.sortOrder
      );

      return makeAuthedRequest(`/ssf-sort/sort`, {
        method: "put",
        body: JSON.stringify(changed),
      });
    },
    {
      onSuccess: () => {
        setOriginals(fixData(categories));
        client.invalidateQueries(["cms", "ssf-sort"]);
      },
    }
  );

  return {
    isChanged: !isEqual(categories, fixData(originals)),
    isSuccess,
    isLoading,
    isError,
    categories,
    setCategories,
    error,
    saveMutation,
    reset,
  };
}
